/**
 * AssessmentStep - External assessment with continuous proctoring
 *
 * Loads ReallyAvatar in an iframe for the assessment step.
 * Monitors integrity via face detection and behavioral checks.
 * Completes when an external system signals completion via API or postMessage.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAssessmentStore } from '../../../store/assessmentStore'
import { useProctoringConfigStore, getProctoringConfig } from '../../../store/proctoringConfigStore'
import { useAssessmentDefinitionStore } from '../../../store/assessmentDefinitionStore'
import { useProctoring } from '../../../hooks/useProctoring'
import { useEscalatingWarnings } from '../../../hooks/useEscalatingWarnings'
import { useAssessmentTimeLimit, formatRemainingMs } from '../../../hooks/useAssessmentTimeLimit'
import { useAvatar } from '../../Avatar'
import Webcam from '../../Webcam'
import { deliverIntegrityReport } from '../../../services/integrityReport'
import { waitForAssessmentReport } from '../../../services/assessmentReport'
import { isAssessmentCompletionMessage } from '@really-assess/shared/integrations'
import type { IntegrityCompletionStatus } from '@really-assess/shared/integrations'
import { enterFullscreen, isFullscreenActive, isFullscreenSupported } from '../../../utils/fullscreen'
const FACE_API_URL = 'https://reallyassess-face-api-dev.techtree.workers.dev'
export default function AssessmentStep() {
  const {
    sessionId,
    sessionToken,
    learner,
    proctoringStatus,
    setAssessmentStartTime,
    assessmentStartTime,
    timeLimitStartedAt,
    setTimeLimitStartedAt,
    setCompletionOutcome,
    addProctoringEvent,
    setStep,
    calculateResult,
    setAssessmentCompleted,
    setAssessmentReport,
  } = useAssessmentStore()
  const config = useProctoringConfigStore()
  const proctoringConfig = getProctoringConfig()
  const assessmentSlug = useAssessmentDefinitionStore((s) => s.slug)
  const { speak, clearQueue } = useAvatar()
  const [hasStarted, setHasStarted] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [needsFullscreenTap, setNeedsFullscreenTap] = useState(false)
  const [completingMessage, setCompletingMessage] = useState('Finalizing integrity score...')
  const introSpoken = useRef(false)
  const completionHandled = useRef(false)
  const warnings = useEscalatingWarnings()
  useProctoring({
    onWarning: (message) => {
      let warningType: 'face' | 'multiple' | 'tab' | 'fullscreen' | 'general' = 'general'
      if (message.includes('fullscreen')) {
        warningType = 'fullscreen'
      } else if (message.includes('people') || message.includes('multiple')) {
        warningType = 'multiple'
      } else if (message.includes('face') || message.includes("can't see") || message.includes("can't detect")) {
        warningType = 'face'
      } else if (message.includes('tab') || message.includes('page')) {
        warningType = 'tab'
      }
      // Fullscreen exits bypass the shared cooldown so each re-exit is shown.
      // Face/tab keep the normal cooldown to avoid spam.
      warnings.warn(message, warningType, {
        bypassCooldown: warningType === 'fullscreen',
      })
    },
    onResolved: () => {
      // Face visibility restored only
      if (warnings.hadWarning()) {
        warnings.markResolved()
        speak("Great, I can see you clearly now. Please continue your assessment.")
      }
    },
    onFullscreenRestored: () => {
      warnings.clearFullscreenWarning()
      setNeedsFullscreenTap(false)
    },
    onFullscreenExited: () => {
      // Lockdown: block the assessment until the learner re-enters fullscreen
      // (browsers only allow requestFullscreen from a user gesture).
      setNeedsFullscreenTap(true)
    },
    onSuspend: () => {
      handleAssessmentComplete('suspended')
    },
  })
  const buildAssessmentUrl = useCallback(() => {
    const { externalAssessmentUrl } = getProctoringConfig()
    if (!externalAssessmentUrl) return ''
    try {
      const url = new URL(externalAssessmentUrl)
      if (sessionId) url.searchParams.set('sessionId', sessionId)
      if (learner?.id) url.searchParams.set('userId', learner.id)
      if (learner?.email) url.searchParams.set('userEmail', learner.email)
      if (learner?.name) url.searchParams.set('userName', learner.name)
      if (assessmentSlug) url.searchParams.set('assessmentSlug', assessmentSlug)
      return url.toString()
    } catch {
      return externalAssessmentUrl
    }
  }, [sessionId, learner, assessmentSlug])
  const handleAssessmentComplete = useCallback(async (status: IntegrityCompletionStatus = 'completed') => {
    if (completionHandled.current) return
    completionHandled.current = true
    setIsCompleting(true)
    setCompletingMessage('Finalizing integrity score...')
    clearQueue()
    setCompletionOutcome(status)
    setAssessmentCompleted(true)
    calculateResult()
    const state = useAssessmentStore.getState()
    const assessmentId = assessmentSlug || state.learner?.id || sessionId || 'unknown'
    await deliverIntegrityReport(
      {
        userId: state.learner?.id || sessionId || 'unknown',
        userName: state.learner?.name,
        userEmail: state.learner?.email,
        assessmentId,
        assessmentSlug: assessmentSlug || undefined,
        sessionId: sessionId || undefined,
        integrityScore: state.proctoringStatus.score,
        completionStatus: status,
        timestamp: new Date().toISOString(),
        violations: state.proctoringStatus.violations,
      },
      sessionId && sessionToken ? { id: sessionId, token: sessionToken } : undefined
    )

    // Wait for ReallyAvatar report webhook before Results so PDF download is ready.
    // Agent should submit report before complete; allow time for network + storage.
    if (sessionId && sessionToken) {
      setCompletingMessage('Preparing your assessment report...')
      const reportTimeoutMs = status === 'completed' ? 45_000 : 15_000
      const report = await waitForAssessmentReport(sessionId, sessionToken, {
        timeoutMs: reportTimeoutMs,
        intervalMs: 1500,
      })
      setAssessmentReport(report)
      if (!report) {
        console.warn('[Assessment] Report not available before Results navigation')
      }
    } else {
      setAssessmentReport(null)
    }

    setStep('results')
  }, [
    clearQueue,
    setCompletionOutcome,
    setAssessmentCompleted,
    calculateResult,
    sessionId,
    sessionToken,
    assessmentSlug,
    setAssessmentReport,
    setStep,
  ])
  const handleTimeLimitWarning = useCallback((remainingMs: number) => {
    const message = `You have ${formatRemainingMs(remainingMs)} remaining to complete your assessment.`
    warnings.warn(message, 'general')
    speak(message)
  }, [speak, warnings])
  const handleTimeLimitExpired = useCallback(() => {
    addProctoringEvent({
      type: 'time_limit_exceeded',
      timestamp: Date.now(),
      details: 'Assessment time limit reached',
    })
    speak('Your time has expired. This assessment is now ending.')
    handleAssessmentComplete('timed_out')
  }, [addProctoringEvent, speak, handleAssessmentComplete])
  const handleIframeLoad = useCallback(() => {
    if (useAssessmentStore.getState().timeLimitStartedAt === null) {
      setTimeLimitStartedAt(Date.now())
    }
  }, [setTimeLimitStartedAt])
  const { countdownLabel, remainingMs } = useAssessmentTimeLimit({
    config: proctoringConfig.timeLimit,
    startedAt: timeLimitStartedAt,
    active: proctoringConfig.timeLimit.enabled && timeLimitStartedAt !== null && hasStarted && !isCompleting,
    onWarning: handleTimeLimitWarning,
    onTimeout: handleTimeLimitExpired,
  })

  const browserLockdown = proctoringConfig.browserLockdown
  useEffect(() => {
    if (!browserLockdown) {
      setNeedsFullscreenTap(false)
      return
    }
    if (!isFullscreenSupported()) return
    if (isFullscreenActive()) return

    const timer = setTimeout(() => {
      void enterFullscreen().then((entered) => {
        if (!entered && !isFullscreenActive()) {
          setNeedsFullscreenTap(true)
        }
      })
    }, 300)

    return () => clearTimeout(timer)
  }, [browserLockdown])

  const handleEnterFullscreen = () => {
    void enterFullscreen().then((entered) => {
      if (entered) setNeedsFullscreenTap(false)
    })
  }

  useEffect(() => {
    if (!assessmentStartTime) {
      setAssessmentStartTime(Date.now())
    }
    if (sessionId && sessionToken) {
      fetch(`${FACE_API_URL}/api/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({ state: 'active' }),
      }).catch((err) => console.error('[Assessment] Failed to activate session:', err))
    }
  }, [assessmentStartTime, setAssessmentStartTime, sessionId, sessionToken])
  useEffect(() => {
    // Start assessment UI immediately — do not wait for the first face check
    // (webcam remount can take a few seconds and previously blocked the iframe).
    if (!introSpoken.current) {
      introSpoken.current = true
      setHasStarted(true)
      setTimeout(() => {
        speak("Your assessment is loading in the window on screen. I'll monitor you throughout to ensure integrity. When you finish in ReallyAvatar, your integrity score will be calculated and sent back automatically.")
      }, 800)
    }
  }, [speak])
  useEffect(() => {
    if (!hasStarted || !sessionId || !sessionToken || isCompleting) return
    const pollCompletion = async () => {
      try {
        const response = await fetch(`${FACE_API_URL}/api/sessions/${sessionId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        })
        if (!response.ok) return
        const data = await response.json() as { session?: { state: string } }
        if (data.session?.state === 'completed') {
          handleAssessmentComplete('completed')
        }
      } catch (err) {
        console.error('[Assessment] Completion poll error:', err)
      }
    }
    const interval = setInterval(pollCompletion, config.completionPollIntervalMs)
    return () => clearInterval(interval)
  }, [hasStarted, sessionId, sessionToken, isCompleting, config.completionPollIntervalMs, handleAssessmentComplete])
  useEffect(() => {
    if (!hasStarted) return
    const handleMessage = (event: MessageEvent) => {
      if (isAssessmentCompletionMessage(event.data, sessionId ?? undefined)) {
        handleAssessmentComplete('completed')
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [hasStarted, sessionId, handleAssessmentComplete])
  const assessmentUrl = buildAssessmentUrl()
  const showCountdown = proctoringConfig.timeLimit.enabled && proctoringConfig.timeLimit.showCountdown && countdownLabel
  const countdownUrgent = remainingMs !== null && remainingMs <= 5 * 60 * 1000
  return (
    <div className="flex-1 flex flex-col h-full min-h-0 relative overflow-hidden">
      <div className="fixed left-[-9999px] top-0 w-[640px] h-[480px] opacity-0 pointer-events-none overflow-hidden" aria-hidden>
        <Webcam showCapture={false} className="w-full h-full" />
      </div>
      {browserLockdown && needsFullscreenTap && !isCompleting && (
        <div className="absolute inset-0 z-40 bg-assess-bg/95 flex items-center justify-center p-6">
          <div className="glass-card p-8 max-w-md text-center space-y-4">
            <h2 className="text-xl font-bold text-white">Fullscreen required</h2>
            <p className="text-gray-400 text-sm">
              This assessment runs in fullscreen mode for secure proctoring. Tap below to continue.
            </p>
            <button
              type="button"
              onClick={() => void handleEnterFullscreen()}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 rounded-lg font-medium transition-colors"
            >
              Enter Fullscreen
            </button>
          </div>
        </div>
      )}
      {hasStarted && (
        <div className="absolute bottom-4 left-4 z-20 bg-assess-card/90 border border-assess-border rounded-lg px-3 py-2 flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs text-gray-400">Monitoring</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-400">Integrity: </span>
            <span className={`font-bold ${proctoringStatus.score >= config.passingScore ? 'text-green-400' : 'text-yellow-400'}`}>
              {proctoringStatus.score}%
            </span>
          </div>
          {showCountdown && (
            <div className="text-sm border-l border-assess-border pl-3">
              <span className="text-gray-400">Time left: </span>
              <span className={`font-mono font-bold ${countdownUrgent ? 'text-red-400' : 'text-white'}`}>
                {countdownLabel}
              </span>
            </div>
          )}
        </div>
      )}
      {!hasStarted && (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="glass-card p-6 border-primary-500/50 max-w-md">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <div>
                <p className="text-primary-300 font-medium text-lg">Loading assessment...</p>
                <p className="text-gray-400 text-sm">Preparing your session and camera monitoring.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {isCompleting && (
        <div className="absolute inset-0 bg-assess-bg/80 z-30 flex items-center justify-center">
          <div className="text-center px-6">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">{completingMessage}</p>
            <p className="text-gray-400 text-sm mt-2">Please wait — do not close this window.</p>
          </div>
        </div>
      )}
      {hasStarted && assessmentUrl && (
        <iframe
          key={sessionId ?? assessmentUrl}
          src={assessmentUrl}
          title="ReallyAvatar Assessment"
          className="flex-1 w-full h-full min-h-0 border-0 bg-white"
          allow="camera; microphone; fullscreen"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
          onLoad={handleIframeLoad}
        />
      )}
      {hasStarted && !assessmentUrl && (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="glass-card p-6 max-w-md text-center">
            <p className="text-red-400 font-medium mb-2">No ReallyAvatar URL configured</p>
            <p className="text-gray-400 text-sm">
              Set the ReallyAvatar URL for this assessment in the admin panel.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

